import React, { useEffect, useState } from 'react';

import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { Controller, useFormContext } from 'react-hook-form';

import type { SourceGroupOption } from '@vlabs/monokit/entities/sources';
import {
  loadGroupOptions,
  STREAM_NAME_PATTERN,
} from '@vlabs/monokit/entities/sources';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useSourceStreamGeneralInfoTranslation } from './i18n';
import st from './SourceStreamGeneralInfo.module.sass';
import type { StreamSettingsFormValues } from './types';

function SourceStreamGeneralInfo(): React.JSX.Element {
  const { t } = useSourceStreamGeneralInfoTranslation();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<StreamSettingsFormValues>();

  const [groupOptions, setGroupOptions] = useState<SourceGroupOption[]>([]);

  useEffect(() => {
    loadGroupOptions().then(setGroupOptions);
  }, []);

  const getSelectedOption = (value: string): SourceGroupOption | undefined => (
    groupOptions.find((option) => option.value === value)
    ?? (value ? { label: value, value } : undefined)
  );

  return (
    <div className={st.Root}>
      <h6 className={st.Title}>{t('title')}</h6>

      <FormFieldGroup>
        <FormFieldGroup.Item
          label={t('fields.name')}
          required
        >
          <Control.Input
            errorMessage={errors.name?.message}
            errors={errors}
            id="name"
            maxLength={128}
            {...register('name', {
              maxLength: 128,
              pattern: { message: t('validation.name_pattern'), value: STREAM_NAME_PATTERN },
              required: t('validation.required'),
            })}
          />
        </FormFieldGroup.Item>

        <FormFieldGroup.Item label={t('fields.description')}>
          <Control.Textarea
            className={st.DescriptionTextarea}
            errors={errors}
            id="description"
            {...register('description', { maxLength: 512 })}
          />
        </FormFieldGroup.Item>

        <FormFieldGroup.Item
          label={t('fields.group')}
          required
        >
          <Controller
            control={control}
            name="group_id"
            render={({ field: { value, onChange } }) => (
              <Select
                classNamePrefix="SourceStreamGroup"
                errorMessage={errors.group_id?.message}
                errors={errors}
                menuPortalTarget={document.body}
                onChange={(option) => onChange(option?.value ?? '')}
                options={groupOptions}
                placeholder={t('fields.group')}
                styles={selectMenuPortalStyles}
                value={getSelectedOption(value)}
              />
            )}
            rules={{ required: t('validation.required') }}
          />
        </FormFieldGroup.Item>
      </FormFieldGroup>
    </div>
  );
}

export { SourceStreamGeneralInfo };
